/* eslint-disable react/prop-types */
import { CheckCircleOutlined, CheckOutlined, InfoCircleOutlined, SwapOutlined, WarningOutlined } from '@ant-design/icons';
import { Button, Spin, message } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { uid } from 'uid';

import { getPublicNfeInfo, submitPublicNfe, uploadPublicNfeFile } from '../../operations/publicNfe';
import { StepManualForm } from './StepManualForm';
import { StepSendType } from './StepSendType';
import { StepUploadForm } from './StepUploadForm';
import { StepUserData } from './StepUserData';
import {
  Card,
  ClientLabel,
  ClientName,
  ClientRow,
  ErrorWrapper,
  Header,
  InfoBanner,
  Logo,
  NotMeButton,
  PageWrapper,
  PoweredBy,
  StepDot,
  StepIndicatorWrapper,
  StepLabel,
  StepLine,
  SuccessWrapper,
  TenantName,
} from './styles';
import type { CurrentStep, CustomerInfo, PageState, SubmitterData, TenantInfo } from './types';

const STORAGE_KEY_PREFIX = 'public_nfe_submitter_';

const getStepNumber = (step: CurrentStep): number => {
  if (step === 1) return 1;
  if (step === 2) return 2;
  return 3;
};

const StepIndicator = ({ currentStep }: { currentStep: CurrentStep }) => {
  const stepNum = getStepNumber(currentStep);

  return (
    <>
      <StepIndicatorWrapper>
        <StepDot $active={stepNum === 1} $completed={stepNum > 1}>
          {stepNum > 1 ? <CheckOutlined style={{ fontSize: 16 }} /> : '1'}
        </StepDot>
        <StepLine $completed={stepNum > 1} />
        <StepDot $active={stepNum === 2} $completed={stepNum > 2}>
          {stepNum > 2 ? <CheckOutlined style={{ fontSize: 16 }} /> : '2'}
        </StepDot>
        <StepLine $completed={stepNum > 2} />
        <StepDot $active={stepNum === 3}>3</StepDot>
      </StepIndicatorWrapper>
      <StepLabel>Passo {stepNum} de 3</StepLabel>
    </>
  );
};

export const PublicNfePage = () => {
  const { tenantId, token } = useParams<{ tenantId: string; token: string }>();
  const [pageState, setPageState] = useState<PageState>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const [customer, setCustomer] = useState<CustomerInfo | null>(null);
  const [tenantInfo, setTenantInfo] = useState<TenantInfo | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [protocol, setProtocol] = useState('');
  const [uniqueId] = useState(uid());

  const [currentStep, setCurrentStep] = useState<CurrentStep>(1);
  const [submitterData, setSubmitterData] = useState<SubmitterData | null>(null);

  const cardRef = useRef<HTMLDivElement>(null);

  const goToStep = (step: CurrentStep) => {
    setCurrentStep(step);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Load submitter data from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY_PREFIX + token);
      if (stored) {
        setSubmitterData(JSON.parse(stored));
      }
    } catch {
      // ignore parse errors
    }
  }, [token]);

  useEffect(() => {
    getPublicNfeInfo(tenantId, token)
      .then((res) => {
        setCustomer(res.data.data.customer);
        setTenantInfo(res.data.data.tenant);
        setPageState('ready');
      })
      .catch((err) => {
        const msg =
          (err && err.response && err.response.data && err.response.data.message) ||
          'Não foi possível carregar o formulário. Verifique o link e tente novamente.';
        setErrorMessage(msg);
        setPageState('error');
      });
  }, [token]);

  const handleStep1Continue = (data: SubmitterData) => {
    setSubmitterData(data);
    try {
      localStorage.setItem(STORAGE_KEY_PREFIX + token, JSON.stringify(data));
    } catch {
      // ignore storage errors
    }
    goToStep(2);
  };

  const handleManualSubmit = (customData: any[], description: string) => {
    if (!submitterData) return;
    setIsSubmitting(true);

    const payload = {
      uid: uniqueId,
      description,
      submitter_name: submitterData.submitter_name,
      custom_data: customData,
    };

    submitPublicNfe(tenantId, token, payload)
      .then((res) => {
        setProtocol(res.data.protocol || '');
        setPageState('success');
      })
      .catch((err) => {
        const msg =
          (err && err.response && err.response.data && err.response.data.message) ||
          'Não foi possível enviar a solicitação. Tente novamente.';
        message.error(msg);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handleUploadSubmit = (file: File, description: string) => {
    if (!submitterData) return;
    setIsSubmitting(true);
    setUploadProgress(0);

    uploadPublicNfeFile(
      tenantId,
      token,
      file,
      submitterData.submitter_name,
      uniqueId,
      description,
      (percent) => setUploadProgress(percent),
    )
      .then((res) => {
        setProtocol(res.data.protocol || '');
        setPageState('success');
      })
      .catch((err) => {
        const msg =
          (err && err.response && err.response.data && err.response.data.message) ||
          'Não foi possível enviar o arquivo. Tente novamente.';
        message.error(msg);
        setUploadProgress(0);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const cnaes = customer && customer.cnaes
    ? customer.cnaes.split(',').map((c) => c.trim()).filter(Boolean)
    : [];

  if (pageState === 'loading') {
    return (
      <PageWrapper>
        <Spin size="large" />
      </PageWrapper>
    );
  }

  if (pageState === 'error') {
    return (
      <PageWrapper>
        <Card>
          <ErrorWrapper>
            <WarningOutlined style={{ fontSize: 56, color: '#faad14' }} />
            <h2>Link inválido</h2>
            <p>{errorMessage}</p>
          </ErrorWrapper>
        </Card>
      </PageWrapper>
    );
  }

  if (pageState === 'success') {
    return (
      <PageWrapper>
        <Card>
          {tenantInfo && (
            <Header>
              {tenantInfo.logo
                ? <Logo src={tenantInfo.logo} alt={tenantInfo.name} />
                : <TenantName>{tenantInfo.name}</TenantName>
              }
            </Header>
          )}
          <SuccessWrapper>
            <CheckCircleOutlined style={{ fontSize: 64, color: '#52c41a' }} />
            <h2>Solicitação enviada!</h2>
            {protocol && (
              <div style={{
                background: '#f6ffed',
                border: '1px solid #b7eb8f',
                borderRadius: 8,
                padding: '12px 20px',
                marginBottom: 8,
                fontSize: 16,
              }}>
                <span style={{ color: 'rgba(0,0,0,0.45)' }}>Protocolo:</span>{' '}
                <strong style={{ fontSize: 18, letterSpacing: 0.5 }}>{protocol}</strong>
              </div>
            )}
            <p>
              Sua solicitação foi recebida com sucesso. Nossa equipe contábil irá processá-la e você será notificado assim que houver atualizações.
            </p>
            <Button
              type="primary"
              size="large"
              block
              onClick={() => {
                setPageState('ready');
                setCurrentStep(2);
                setIsSubmitting(false);
                setProtocol('');
              }}
            >
              Enviar outra solicitação
            </Button>
            <a
              href="/login"
              style={{ marginTop: 12, display: 'inline-block', fontSize: 16, color: 'rgba(0,0,0,0.45)' }}
            >
              Acessar o portal do cliente
            </a>
          </SuccessWrapper>
        </Card>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <Card ref={cardRef}>
        {tenantInfo && (
          <Header>
            {tenantInfo.logo
              ? <Logo src={tenantInfo.logo} alt={tenantInfo.name} />
              : <TenantName>{tenantInfo.name}</TenantName>
            }
          </Header>
        )}

        <ClientRow>
          <div>
            <ClientLabel>Solicitação de NF-e para:</ClientLabel>
            <ClientName>{customer && customer.name}</ClientName>
          </div>
          <NotMeButton
            type="button"
            onClick={() => { window.location.href = '/login'; }}
          >
            <SwapOutlined />
            Não sou eu
          </NotMeButton>
        </ClientRow>

        <StepIndicator currentStep={currentStep} />

        {currentStep === 1 && (
          <StepUserData
            initialValues={submitterData}
            onContinue={handleStep1Continue}
          />
        )}

        {currentStep === 2 && (
          <StepSendType
            hasCnaes={cnaes.length > 0}
            onSelectManual={() => goToStep('3a')}
            onSelectUpload={() => goToStep('3b')}
            onBack={() => goToStep(1)}
          />
        )}

        {currentStep === '3a' && (
          <StepManualForm
            cnaes={cnaes}
            isSubmitting={isSubmitting}
            onSubmit={handleManualSubmit}
            onBack={() => goToStep(2)}
          />
        )}

        {currentStep === '3b' && (
          <StepUploadForm
            isSubmitting={isSubmitting}
            uploadProgress={uploadProgress}
            onSubmit={handleUploadSubmit}
            onBack={() => goToStep(2)}
          />
        )}

        <InfoBanner>
          <InfoCircleOutlined style={{ marginTop: 2 }} />
          <span>
            Para acompanhar o status da solicitação, acesse o{' '}
            <a href="/login">portal do cliente</a>.
          </span>
        </InfoBanner>
      </Card>

      <PoweredBy>
        Powered by <a href="https://contlabs.com.br" target="_blank" rel="noopener noreferrer">Contlabs</a>
      </PoweredBy>
    </PageWrapper>
  );
};
